import React from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import parse, { domToReact } from 'html-react-parser'
import { Guide } from '@/utils/guide'
import { ExternalLinkModal } from '@/components/organisms/external-link-modal'
import { CUSTOM_DESIGN_LINK_CLASS_NAME } from '@/components/molecules/settings/editor/custom-decorator'
import { CUSTOM_DESIGN_BUTTON_CLASS_NAME } from '@/components/molecules/settings/editor/design-custom-button'
import { FrontTabletFormLabel } from '@/components/molecules/front-tablet/input/form-label'
import { FrontTabletSelectBox } from '@/components/molecules/front-tablet/input/select-box'
import { FrontTabletTextArea } from '@/components/molecules/front-tablet/input/text-area'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'

// domNode: { Element } from 'html-react-parser' がコンパイルエラー('Element' is not exported)となるためanyで代替
const parseAndReplaceLink = (htmlText: string) =>
  parse(htmlText, {
    replace: (domNode: any) => {
      if (domNode.type === 'tag' && domNode.name === 'a') {
        if (domNode.attribs.class === CUSTOM_DESIGN_LINK_CLASS_NAME) {
          return <ExternalLinkModal src={domNode.attribs.href}>{domToReact(domNode.children)}</ExternalLinkModal>
        } else if (domNode.attribs.class === CUSTOM_DESIGN_BUTTON_CLASS_NAME) {
          return (
            <ExternalLinkModal src={domNode.attribs.href} type="button">
              {domToReact(domNode.children)}
            </ExternalLinkModal>
          )
        }
      }
    },
  })

const getGuideTitle = (guide: Guide, lang: string) => {
  if (lang === 'ja') return parse(guide.jpTitle)
  if (lang === 'en') return parse(guide.enTitle)
  if (lang === 'ko') return parse(guide.koTitle)
  if (lang === 'zh') return parse(guide.zhTitle)
}

const getGuideContent = (guide: Guide, lang: string) => {
  if (lang === 'ja') return parseAndReplaceLink(guide.jpContent)
  if (lang === 'en') return parseAndReplaceLink(guide.enContent)
  if (lang === 'ko') return parseAndReplaceLink(guide.koContent)
  if (lang === 'zh') return parseAndReplaceLink(guide.zhContent)
}

const getGuideOption = (guide: Guide, lang: string) => {
  if (lang === 'ja') return guide.jpInputValue
  if (lang === 'en') return guide.enInputValue
  if (lang === 'ko') return guide.koInputValue
  if (lang === 'zh') return guide.zhInputValue
}

export type CustomCheckinProps = {
  guide: Guide
  selectedInputValue: { customCheckinId: string; value: string } | undefined
  onChangeState: (e: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>, customCheckinId: string) => void
  isEdit?: boolean
}

export const CustomCheckin: React.FC<CustomCheckinProps> = ({ guide, selectedInputValue, onChangeState, isEdit }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const currentValue = selectedInputValue?.value ?? (guide.selectedAnswer && guide.selectedAnswer !== '-' ? guide.selectedAnswer : '')
  return (
    <div>
      <div css={isEdit ? editItemWrapperStyle : itemWrapperStyle}>
        <div css={labelWrapperStyle}>
          <FrontTabletFormLabel
            label={getGuideTitle(guide, lang)}
            required={guide.inputType !== 'none' && Boolean(guide.isRequired)}
            customFontSize={22}
          />
        </div>
        <div css={contentStyle}>{getGuideContent(guide, lang)}</div>
        {guide.inputType === 'textArea' && (
          <div css={inputWrapperStyle}>
            <FrontTabletTextArea
              id={guide.customCheckinId}
              rows={3}
              name="value"
              placeholder={t('Please write your requests')}
              value={currentValue}
              onChange={e => onChangeState(e, guide.customCheckinId)}
            />
          </div>
        )}
        {guide.inputType === 'textArea' ? (
          <div id={guide.customCheckinId + '-error'} css={errorMessage}>
            {t('Required field has not been entered')}
          </div>
        ) : null}
        {guide.inputType === 'selectBox' && (
          <div css={inputWrapperStyle}>
            <FrontTabletSelectBox
              id={guide.customCheckinId}
              name="value"
              value={currentValue}
              onChange={e => onChangeState(e, guide.customCheckinId)}
            >
              <option value="">{t('Please select')}</option>
              {getGuideOption(guide, lang) &&
                getGuideOption(guide, lang).map((option, i) => (
                  <option key={`selectedAnswer${i}`} value={option}>
                    {option}
                  </option>
                ))}
            </FrontTabletSelectBox>
          </div>
        )}
        {guide.inputType === 'selectBox' ? (
          <div id={guide.customCheckinId + '-error'} css={errorMessage}>
            {t('Required field has not been entered')}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const itemWrapperStyle = css({
  '*': {
    fontFamily: 'Noto Sans JP',
  },
  lineHeight: '1.5',
})

const editItemWrapperStyle = css(itemWrapperStyle, {
  borderBottom: '1px solid #F2F2F2',
  padding: '40px 0',
})

const labelWrapperStyle = css({
  marginBottom: 16,
})

const contentStyle = css(
  {
    textAlign: 'left',
    fontSize: 18,
    color: '#272727',
    letterSpacing: '1.6px',
    lineHeight: 1.5,
    fontWeight: 500,
    figure: {
      '>img': { maxWidth: '100%' },
    },
    h1: {
      fontWeight: 'bold',
      fontSize: 18,
      marginBottom: 16,
      lineHeight: 1.5,
      color: '#272727',
      letterSpacing: '1.8px',
    },
    h2: {
      textAlign: 'left',
      fontSize: 20,
      marginBottom: 16,
      lineHeight: 1.5,
      color: '#272727',
    },
    p: {
      textAlign: 'left',
      fontSize: 18,
      color: '#272727',
      letterSpacing: '1.6px',
      lineHeight: 1.5,
      fontWeight: 500,
      strong: {
        fontWeight: 'bold',
      },
    },
    br: {
      display: 'block',
      content: '""',
      margin: '5px 0',
    },
    ul: {
      marginLeft: '40px',
      li: {
        listStyleType: 'disc',
      },
    },
    ol: {
      marginLeft: '40px',
      li: {
        listStyleType: 'decimal',
      },
    },
    li: {
      textAlign: 'left',
      fontSize: '18px',
      lineHeight: 1.5,
    },
    strong: {
      fontWeight: 'bold',
    },
    a: {
      fontSize: '16px',
      letterSpacing: '1.6px',
      lineHeight: '26px',
      color: '#f2a40b',
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      fontSize: FrontTabletResponsiveToken.bodyFontSize.landscape13,
      'h1, p, li': { fontSize: FrontTabletResponsiveToken.bodyFontSize.landscape13 },
      h2: { fontSize: FrontTabletResponsiveToken.headingFontSize.landscape13 },
      a: { fontSize: 20 },
    },
  }),
)

const errorMessage = css({
  visibility: 'hidden',
  fontSize: '14px',
  fontWeight: 'bold',
  marginTop: '16px',
  textAlign: 'left',
  color: '#FF2700',
})

const inputWrapperStyle = css({
  marginTop: 24,
})
